import { Alert, Button, Card, Col, Form, Input, Row, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getApi, postApi, putApi } from "redux/apis";
import { encryptFieldWithPublicKey } from "utils/emailConfigAsymmetricCrypto";
import { deobfuscateEmailConfigField } from "utils/emailConfigGetObfuscation";
import { toastText } from "utils/utils";
import { ROLE_NAME } from "components/Global/AuthLayout";

interface EmailConfigFormValues {
  fromEmail: string;
  contactSalesEmail: string;
  smtpHost: string;
  smtpPort: string;
  smtpUsername: string;
  smtpPassword: string;
  secure: boolean;
  tls: boolean;
}

const DEFAULT_VALUES: EmailConfigFormValues = {
  fromEmail: "",
  contactSalesEmail: "",
  smtpHost: "",
  smtpPort: "2525",
  smtpUsername: "",
  smtpPassword: "",
  secure: false,
  tls: true,
};

const EmailConfiguration = () => {
  const [form] = Form.useForm<EmailConfigFormValues>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [hasSavedConfig, setHasSavedConfig] = useState(false);
  const [hasSavedPassword, setHasSavedPassword] = useState(false);
  const profileData = useSelector((state: any) => state?.userProfile?.data);
  const roleName = profileData?.companyRoles?.[0]?.role?.roleName;
  const canManageEmailConfig = roleName === ROLE_NAME.SUPER_ADMIN;

  const setFormData = (data: any) => {
    const saved = Boolean(data?.hasSavedConfig);
    const decode = (v: unknown) => {
      if (!saved || v == null || v === "") return "";
      return deobfuscateEmailConfigField(String(v));
    };
    const normalizedData: EmailConfigFormValues = {
      fromEmail: decode(data?.fromEmail),
      contactSalesEmail: decode(data?.contactSalesEmail),
      smtpHost: decode(data?.smtpHost),
      smtpPort: saved
        ? decode(data?.smtpPort) || DEFAULT_VALUES.smtpPort
        : String(data?.smtpPort ?? data?.port ?? DEFAULT_VALUES.smtpPort),
      smtpUsername: decode(data?.smtpUsername),
      smtpPassword: "",
      secure: Boolean(data?.secure),
      tls: data?.tls === undefined ? true : Boolean(data?.tls),
    };
    setHasSavedPassword(Boolean(data?.hasSavedPassword));
    form.setFieldsValue(normalizedData);
  };

  const fetchEmailConfig = async () => {
    setIsLoading(true);
    try {
      const response = await getApi("/email-config");
      const payload = response?.data?.data ?? response?.data ?? {};
      setHasSavedConfig(Boolean(payload?.hasSavedConfig));
      setFormData(payload);
    } catch (error: any) {
      if (error?.response?.status !== 404) {
        toastText(
          error?.response?.data?.error?.message ||
            "Failed to load email configuration",
          "error"
        );
      }
      setHasSavedConfig(false);
      setHasSavedPassword(false);
      form.setFieldsValue(DEFAULT_VALUES);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!roleName) return;
    if (!canManageEmailConfig) {
      toastText("You don't have permission to access Email Configuration", "error");
      navigate("/");
      return;
    }
    form.setFieldsValue(DEFAULT_VALUES);
    fetchEmailConfig();
  }, [canManageEmailConfig, roleName]);

  const getPayloadFromForm = (values: EmailConfigFormValues) => {
    return values;
  };

  const getEncryptedPayloadFromForm = async (values: EmailConfigFormValues) => {
    return {
      encryptedFromEmail: await encryptFieldWithPublicKey(values.fromEmail?.trim()),
      encryptedContactSalesEmail: await encryptFieldWithPublicKey(
        values.contactSalesEmail?.trim()
      ),
      encryptedSmtpHost: await encryptFieldWithPublicKey(values.smtpHost?.trim()),
      smtpPort: Number(values.smtpPort),
      encryptedSmtpUsername: await encryptFieldWithPublicKey(
        values.smtpUsername?.trim()
      ),
      encryptedSmtpPassword: await encryptFieldWithPublicKey(
        values.smtpPassword?.trim()
      ),
      secure: Boolean(values.secure),
      tls: Boolean(values.tls),
    };
  };

  const onSave = async (values: EmailConfigFormValues) => {
    setIsSaving(true);
    try {
      const encryptedPayload = await getEncryptedPayloadFromForm(
        getPayloadFromForm(values)
      );
      await putApi("/email-config", encryptedPayload);
      setHasSavedConfig(true);
      form.setFieldValue("smtpPassword", "");
      toastText("Email configuration saved successfully", "success");
    } catch (error: any) {
      console.error("Email configuration save failed", error);
      toastText(
        error?.response?.data?.error?.message ||
          error?.message ||
          "Failed to save email configuration",
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const onTestConnection = async () => {
    try {
      const values = await form.validateFields();
      setIsTesting(true);
      const encryptedPayload = await getEncryptedPayloadFromForm(
        getPayloadFromForm(values)
      );
      const response = await postApi("/email-config/test", encryptedPayload);
      const result = response?.data?.data ?? response?.data ?? {};
      const recipient = result?.testRecipient;
      toastText(
        recipient
          ? `SMTP verified and test email sent to ${recipient}`
          : "SMTP verified and test email sent successfully",
        "success"
      );
    } catch (error: any) {
      if (!error?.errorFields) {
        console.error("Email configuration test failed", error);
        toastText(
          error?.response?.data?.error?.message ||
            error?.message ||
            "Failed to test email settings",
          "error"
        );
      }
    } finally {
      setIsTesting(false);
    }
  };

  const renderRequiredLabel = (label: string) => (
    <span style={{ fontSize: "14px", fontWeight: 600 }}>
      {label} <span style={{ color: "#ff4d4f" }}>*</span>
    </span>
  );

  return (
    <div>
      <Typography.Title level={3} style={{ fontWeight: 700, marginBottom: 16 }}>
        Email Configuration
      </Typography.Title>
      <Card loading={isLoading}>
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Form
            form={form}
            layout="vertical"
            initialValues={DEFAULT_VALUES}
            validateTrigger="onChange"
            requiredMark={false}
            onFinish={onSave}
          >
            <Row gutter={[16, 14]}>
              <Col xs={24} md={12}>
                <Form.Item
                  label={renderRequiredLabel("From Email")}
                  name="fromEmail"
                  rules={[
                    {
                      required: true,
                      message: "Please enter from email",
                      validateTrigger: "onSubmit",
                    },
                    {
                      type: "email",
                      message: "Please enter valid email",
                      validateTrigger: "onChange",
                    },
                  ]}
                >
                  <Input placeholder="abc@domain.com" />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={renderRequiredLabel("Contact Sales Email")}
                  name="contactSalesEmail"
                  rules={[
                    {
                      required: true,
                      message: "Please enter contact sales email",
                      validateTrigger: "onSubmit",
                    },
                    {
                      type: "email",
                      message: "Please enter valid contact sales email",
                      validateTrigger: "onChange",
                    },
                  ]}
                >
                  <Input placeholder="contactsales@domain.com" />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={renderRequiredLabel("SMTP Host")}
                  name="smtpHost"
                  rules={[
                    {
                      required: true,
                      message: "Please enter SMTP host",
                      validateTrigger: "onSubmit",
                    },
                  ]}
                >
                  <Input placeholder="Host Name of the SMTP server" />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={renderRequiredLabel("SMTP Port")}
                  name="smtpPort"
                  rules={[
                    {
                      required: true,
                      message: "Please enter SMTP port",
                      validateTrigger: "onSubmit",
                    },
                    {
                      pattern: /^\d+$/,
                      message: "SMTP port must contain numbers only",
                      validateTrigger: "onChange",
                    },
                    {
                      validator: (_, value) => {
                        const port = Number(value);
                        if (!value || Number.isNaN(port) || port < 1 || port > 65535) {
                          return Promise.reject(
                            new Error("SMTP port must be between 1 and 65535")
                          );
                        }
                        return Promise.resolve();
                      },
                      validateTrigger: "onChange",
                    },
                  ]}
                  getValueFromEvent={(e) =>
                    String(e?.target?.value || "").replace(/\D/g, "")
                  }
                >
                  <Input
                    inputMode="numeric"
                    maxLength={5}
                    placeholder="e.g. 587"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={renderRequiredLabel("SMTP Username")}
                  name="smtpUsername"
                  rules={[
                    {
                      required: true,
                      message: "Please enter SMTP username",
                      validateTrigger: "onSubmit",
                    },
                  ]}
                >
                  <Input placeholder="SMTP username" />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={renderRequiredLabel("SMTP Password")}
                  name="smtpPassword"
                  rules={[
                    {
                      required: true,
                      message: "Please enter SMTP password",
                      validateTrigger: "onSubmit",
                    },
                  ]}
                >
                  <Input.Password
                    placeholder={
                      hasSavedPassword
                        ? "enter SMTP password to save or test"
                        : "Enter SMTP password"
                    }
                  />
                </Form.Item>
              </Col>
            </Row>

            <Space style={{ marginTop: 20 }}>
              <Button onClick={onTestConnection} loading={isTesting}>
                Test Connection
              </Button>
              <Button type="primary" htmlType="submit" loading={isSaving}>
                Save
              </Button>
            </Space>
          </Form>
        </Space>
      </Card>
    </div>
  );
};

export default EmailConfiguration;
